import { useState } from "react";
import {
  Badge,
  Box,
  Divider,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Text,
  VStack,
} from "@chakra-ui/react";
import { InfoOutlineIcon } from "@chakra-ui/icons";
import { formatPages, pageNumbers } from "../utils";

// How well the extracted value is backed by the source document.
const MATCH_COLORS = {
  exact: "green",
  verbatim: "green",
  all_words: "teal",
  all_words_present: "teal",
  partial: "yellow",
  summary_overlap: "yellow",
  derived: "purple",
  unverified: "gray",
  no_citation_present: "orange",
  not_found: "red",
};

// `source: metadata.csv` just names the ingest spreadsheet, which tells a reader
// nothing, so that row is dropped. Any other source value is shown.
const HIDDEN_SOURCE_VALUES = new Set(["metadata.csv"]);

// Shown in the entry header/quote, so the generic field list skips them.
const HANDLED_FIELDS = new Set([
  "quote",
  "match",
  "page",
  "pages",
  "page_match",
  "quote_fidelity",
]);

const humanize = (key) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Returns a printable string, or null when there is nothing worth showing.
const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return null;

  if (Array.isArray(value)) {
    const items = value
      .map(formatValue)
      .filter((v) => v !== null && v !== undefined);
    return items.length > 0 ? items.join(", ") : null;
  }

  if (typeof value === "boolean") return value ? "Yes" : "No";

  if (typeof value === "object") {
    // e.g. grades: { Au_g_t: 1.67 } — those keys carry their own units, so they
    // are shown verbatim rather than humanized.
    const pairs = Object.entries(value)
      .map(([k, v]) => [k, formatValue(v)])
      .filter(([, v]) => v !== null)
      .map(([k, v]) => `${k}: ${v}`);
    return pairs.length > 0 ? pairs.join(", ") : null;
  }

  return String(value);
};

const MatchBadge = ({ value }) => {
  if (!value) return null;
  return (
    <Badge
      colorScheme={MATCH_COLORS[value] || "gray"}
      fontSize="10px"
      textTransform="none"
    >
      {humanize(value)}
    </Badge>
  );
};

const ProvenanceEntry = ({ entry, onPageClick }) => {
  const primaryPage = pageNumbers(entry.page)[0];
  const pageLabel = formatPages(entry.page);
  const allPagesLabel = formatPages(entry.pages);
  // `pages` lists every page the value appears on; only worth showing when it
  // adds something beyond the primary citation page.
  const showAllPages =
    allPagesLabel &&
    pageNumbers(entry.pages).length > 1 &&
    allPagesLabel !== pageLabel;

  const fields = Object.entries(entry)
    .filter(([key]) => !HANDLED_FIELDS.has(key))
    .filter(
      ([key, value]) => !(key === "source" && HIDDEN_SOURCE_VALUES.has(value))
    )
    .map(([key, value]) => [key, formatValue(value)])
    .filter(([, value]) => value !== null);

  return (
    <VStack align="stretch" spacing={2}>
      <HStack spacing={2} flexWrap="wrap">
        <MatchBadge value={entry.match} />
        {entry.quote_fidelity && entry.quote_fidelity !== entry.match && (
          <MatchBadge value={entry.quote_fidelity} />
        )}
        {pageLabel &&
          (primaryPage !== undefined && onPageClick ? (
            <Badge
              as="button"
              type="button"
              colorScheme="amber"
              fontSize="10px"
              textTransform="none"
              cursor="pointer"
              _hover={{ textDecoration: "underline" }}
              onClick={() => onPageClick(entry.page)}
              title="Open this page in the PDF"
            >
              {pageLabel}
            </Badge>
          ) : (
            <Badge colorScheme="amber" fontSize="10px" textTransform="none">
              {pageLabel}
            </Badge>
          ))}
      </HStack>

      {entry.quote && (
        <Box
          borderLeft="3px solid"
          borderColor="amber.300"
          bg="slate.50"
          pl={3}
          pr={2}
          py={2}
          borderRadius="sm"
        >
          <Text fontSize="xs" color="slate.700" fontStyle="italic">
            “{entry.quote}”
          </Text>
        </Box>
      )}

      {showAllPages && (
        <Text fontSize="10px" color="slate.500">
          Also appears on {allPagesLabel.toLowerCase()}
        </Text>
      )}

      {fields.length > 0 && (
        <VStack align="stretch" spacing={1}>
          {fields.map(([key, value]) => (
            <HStack key={key} align="start" spacing={3}>
              <Text
                fontSize="10px"
                color="slate.500"
                textTransform="uppercase"
                letterSpacing="wide"
                minW="38%"
                flexShrink={0}
              >
                {humanize(key)}
              </Text>
              <Text fontSize="xs" color="slate.800" wordBreak="break-word">
                {value}
              </Text>
            </HStack>
          ))}
        </VStack>
      )}
    </VStack>
  );
};

/**
 * A small marker placed next to an attribute on the report page. Hovering shows
 * the evidence behind that attribute; clicking pins the card open so the quote
 * can be selected and the page link followed.
 */
export const ProvenanceMarker = ({
  label,
  entries,
  onPageClick,
  color = "slate.400",
  hoverColor = "amber.500",
}) => {
  // `hovering` is driven by Chakra's hover trigger (pointer and keyboard focus).
  // `pinned` is ours: a click keeps the card up after the pointer moves away, so
  // the quote can be selected and the page link followed.
  const [pinned, setPinned] = useState(false);
  const [hovering, setHovering] = useState(false);

  if (!entries || entries.length === 0) return null;

  const isOpen = pinned || hovering;
  const close = () => {
    setPinned(false);
    setHovering(false);
  };

  return (
    <Popover
      // trigger="hover" is deliberate: it adds no onClick of its own, so our
      // click-to-pin can never be turned into a close by Chakra's toggle.
      trigger="hover"
      isOpen={isOpen}
      onOpen={() => setHovering(true)}
      onClose={() => setHovering(false)}
      openDelay={80}
      closeDelay={200}
      placement="top-start"
      closeOnBlur={false}
      isLazy
    >
      <PopoverTrigger>
        <Box
          as="button"
          type="button"
          display="inline-flex"
          alignItems="center"
          lineHeight="1"
          ml={1}
          verticalAlign="middle"
          color={isOpen ? hoverColor : color}
          _hover={{ color: hoverColor }}
          onClick={() => setPinned(true)}
          onKeyDown={(e) => {
            if (e.key === "Escape") close();
          }}
          aria-label={`Show where ${label} came from`}
          title={`Show where ${label} came from`}
        >
          <InfoOutlineIcon boxSize={3} />
        </Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          w="380px"
          maxW="90vw"
          bg="white"
          borderColor="slate.200"
          shadow="xl"
          // The default scale/fade can be left part-way through when the open
          // state flips, leaving a see-through card over page text. These
          // variants mirror Chakra's own (including the visibility handling it
          // merges in, which a `variants` override would otherwise drop) but
          // snap instead of animating, so the card is always fully opaque.
          motionProps={{
            variants: {
              enter: {
                opacity: 1,
                scale: 1,
                visibility: "visible",
                transition: { duration: 0 },
              },
              exit: {
                opacity: 0,
                scale: 0.95,
                transition: { duration: 0 },
                transitionEnd: { visibility: "hidden" },
              },
            },
          }}
        >
          <PopoverArrow bg="white" />
          <PopoverCloseButton onClick={close} />
          <PopoverHeader borderColor="slate.200" pr={8}>
            <Text fontSize="xs" fontWeight="bold" color="slate.700">
              {label}
            </Text>
            <Text fontSize="10px" color="slate.500">
              {entries.length === 1 ? "1 source" : `${entries.length} sources`}{" "}
              from the report
            </Text>
          </PopoverHeader>
          <PopoverBody maxH="340px" overflowY="auto">
            <VStack align="stretch" spacing={3} divider={<Divider />}>
              {entries.map((entry, idx) => (
                <ProvenanceEntry
                  key={idx}
                  entry={entry}
                  onPageClick={onPageClick}
                />
              ))}
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
